import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { isTokenPresent, logout } from "../core/auth";
import { isRouteAllowed } from "../utils/roleHelper";
import { getValidate, getLastGoodValidate } from "../core/authValidate";
import s from "./ProtectedRoute.module.scss";

// Esiti possibili (come gestionale-gls):
const LOGIN = "login";
const BACK = "back";
const NO_AZIENDA = "no-azienda";
const RETRY = "retry"; // server irraggiungibile: NON è una sessione scaduta

const LAST_GOOD_GRACE_MS = 10 * 60_000;
const RETRY_BACKOFF_MS = [2000, 5000, 10_000, 30_000];

function ConnectionRetry({ message, attempt, onRetry }) {
  return (
    <div className={s.retry}>
      <h2>Connessione al server non disponibile</h2>
      <p>{message}</p>
      <p className={s.retryHint}>
        La sessione è ancora attiva. Riprovo da solo{attempt > 0 ? ` (tentativo ${attempt})` : ""}…
      </p>
      <button type="button" className="pr-btn pr-btn-confirm" onClick={onRetry}>
        Riprova ora
      </button>
    </div>
  );
}

export default function ProtectedRoute({ children }) {
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [redirect, setRedirect] = useState(LOGIN);
  const [retryInfo, setRetryInfo] = useState({ message: "", attempt: 0 });
  const [nonce, setNonce] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const attemptRef = useRef(0);

  const apply = useCallback(
    (data) => {
      if (!data.valid) {
        setRedirect(LOGIN);
        setValid(false);
        return;
      }
      if (!data.idAzienda) {
        setRedirect(NO_AZIENDA);
        setValid(false);
        return;
      }
      if (!isRouteAllowed(location.pathname, { isAdmin: data.isAdmin, pages: data.pages })) {
        setRedirect(BACK);
        setValid(false);
        return;
      }
      setValid(true);
    },
    [location.pathname],
  );

  useEffect(() => {
    let mounted = true;
    let timer = null;
    if (!isTokenPresent()) {
      setRedirect(LOGIN);
      setValid(false);
      setChecking(false);
      return undefined;
    }
    getValidate()
      .then((data) => {
        if (!mounted) return;
        attemptRef.current = 0;
        apply(data);
      })
      .catch((err) => {
        if (!mounted) return;
        const kind = err?.prKind || "transient";
        if (kind === "forbidden") {
          setValid(false);
          setRedirect(BACK);
          return;
        }
        if (kind === "auth") {
          setValid(false);
          setRedirect(LOGIN);
          if (err?.response?.status !== 401) logout("Sessione scaduta o non valida. Accedi di nuovo.");
          return;
        }
        const lastGood = getLastGoodValidate();
        if (lastGood && lastGood.ageMs < LAST_GOOD_GRACE_MS) {
          apply(lastGood.data);
          return;
        }
        const attempt = attemptRef.current + 1;
        attemptRef.current = attempt;
        setValid(false);
        setRetryInfo({ message: err?.prMessage || "Non è stato possibile contattare il server.", attempt });
        setRedirect(RETRY);
        timer = setTimeout(
          () => mounted && setNonce((n) => n + 1),
          RETRY_BACKOFF_MS[Math.min(attempt - 1, RETRY_BACKOFF_MS.length - 1)],
        );
      })
      .finally(() => mounted && setChecking(false));
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname, nonce, apply]);

  useEffect(() => {
    // Rotta non consentita: si torna indietro, o alla prima pagina consentita.
    if (!checking && !valid && redirect === BACK) {
      if (window.history.length > 1) navigate(-1);
      else navigate("/", { replace: true });
    }
  }, [checking, valid, redirect, navigate]);

  if (checking) return null;
  if (!valid && redirect === RETRY) {
    return (
      <ConnectionRetry
        message={retryInfo.message}
        attempt={retryInfo.attempt}
        onRetry={() => {
          attemptRef.current = 0;
          setChecking(true);
          setNonce((n) => n + 1);
        }}
      />
    );
  }
  if (!valid && redirect === LOGIN) return <Navigate to="/Login" replace />;
  if (!valid && redirect === NO_AZIENDA) return <Navigate to="/pending-approval" replace />;
  return valid ? children : null;
}
